// Web-exclusive Post Job screen - Multi-step wizard modal
// Phase 4: Job posting flow for facilities

import React, { useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import WebModal, { WebModalFooter } from '../../components/web/WebModal';
import { webColors, webBorderRadius } from '../../constants/webStyles';
import { postJob, updateJob } from '../../services/job';
import { getOverview } from '../../services/profile';
import { getLocalizedProfessionName } from '../../utils/professionUtils';

// Step Components
import StepJobType from './steps/StepJobType.web';
import StepJobProfession from './steps/StepJobProfession.web';
import StepJobDetails from './steps/StepJobDetails.web';
import WebJobCreationProgress from '../../components/web/WebJobCreationProgress';
import WebJobCreationSummary from '../../components/web/WebJobCreationSummary';
import { createJobsForCountries } from '../../services/job';

const STEPS = [
  { id: 'type', title: 'Job Type', subtitle: 'Choose the type of position', component: StepJobType },
  { id: 'profession', title: 'Select Profession', subtitle: 'Choose the profession you are hiring for', component: StepJobProfession },
  { id: 'details', title: 'Post Job', subtitle: 'Add a new job for candidates to apply', editTitle: 'Edit Job', editSubtitle: 'Edit the description of job for candidates', component: StepJobDetails },
];

// Map API type number to form jobType string
const getJobTypeString = (typeNum) => {
  const typeMap = { 0: 'locum', 1: 'fulltime', 2: 'parttime', 3: 'contract', 4: 'international' };
  return typeMap[typeNum] || 'fulltime';
};

const PostJobScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const queryClient = useQueryClient();
  const { country } = useSelector((state) => state.user);

  const editJobData = route.params?.editJobData;
  const isEditMode = !!editJobData;
  const isDraft = isEditMode && parseInt(editJobData.type) <= -10;
  // Editing a job that is already live (not a draft)
  const isPostedEdit = isEditMode && !isDraft;

  // Drafts encode the original type as -1<type> (-10 -> 0, -11 -> 1, etc.);
  // posted jobs carry the plain type number.
  const editJobTypeNum = isEditMode
    ? (isDraft ? Math.abs(parseInt(editJobData.type)) - 10 : parseInt(editJobData.type))
    : null;

  // When editing, skip directly to the details step
  const [currentStep, setCurrentStep] = useState(isEditMode ? 2 : 0);

  // Fetch overview data for clinic name
  const { data: overviewData } = useQuery({
    queryKey: ['overview'],
    queryFn: getOverview,
  });
  const clinicName = overviewData?.data?.data?.clinic_name;
  const clinicCity = overviewData?.data?.data?.city;
  const clinicState = overviewData?.data?.data?.state;

  // Build initial form data from editJobData if editing
  const buildInitialFormData = () => {
    if (!editJobData) {
      return {
        jobType: null,
        professionType: null,
        professionSub: null,
        specialization: null,
        profession: null,
        title: '',
        subtitle: '',
        description: '',
        contactPerson: '',
        salary: { min: '', max: '', showSalary: true },
        expirationDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        startDate: null,
        endDate: null,
        startTime: null,
        endTime: null,
        pricePerHour: '',
        targetCountry: '',
        selectedJobCountries: [],
        _isEditMode: false,
      };
    }

    const jobTypeStr = getJobTypeString(editJobTypeNum);
    const isLocum = editJobTypeNum === 0;

    return {
      jobType: jobTypeStr,
      professionType: null,
      professionSub: editJobData.title,
      specialization: editJobData.title,
      profession: editJobData.profession_id ? { id: editJobData.profession_id } : null,
      title: editJobData.title || '',
      subtitle: editJobData.subtitle || '',
      description: editJobData.description || '',
      contactPerson: editJobData.contact_person || '',
      salary: {
        min: editJobData.price ? `${editJobData.price}` : '',
        max: editJobData.price_max ? `${editJobData.price_max}` : '',
        showSalary: !!(editJobData.price || editJobData.price_max),
      },
      expirationDate: (!isLocum && editJobData.start_timestamp)
        ? new Date(editJobData.start_timestamp)
        : new Date(Date.now() + (editJobData.expiration_days || 30) * 24 * 60 * 60 * 1000),
      startDate: isLocum && editJobData.start_timestamp
        ? new Date(editJobData.start_timestamp).toISOString().split('T')[0]
        : null,
      endDate: isLocum && editJobData.end_timestamp
        ? new Date(editJobData.end_timestamp).toISOString().split('T')[0]
        : null,
      startTime: isLocum && editJobData.start_timestamp
        ? new Date(editJobData.start_timestamp).toTimeString().slice(0, 5)
        : null,
      endTime: isLocum && editJobData.end_timestamp
        ? new Date(editJobData.end_timestamp).toTimeString().slice(0, 5)
        : null,
      // `price` is the total for the shift; the form edits the hourly rate
      pricePerHour: isLocum ? `${editJobData.rate_per_hour || editJobData.price || ''}` : '',
      targetCountry: editJobData?.international || '',
      selectedJobCountries: editJobData?.country ? [editJobData.country] : [],
      _isEditMode: true,
    };
  };

  const [formData, setFormData] = useState(buildInitialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [creationProgress, setCreationProgress] = useState(null);
  const [creationResults, setCreationResults] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Job creation/update mutation
  const createJobMutation = useMutation({
    mutationFn: isEditMode ? updateJob : postJob,
    onSuccess: () => {
      queryClient.invalidateQueries(['facilityJobs']);
      queryClient.invalidateQueries(['facilityOverview']);
      queryClient.invalidateQueries(['applications']);
      queryClient.invalidateQueries(['overview']);
      queryClient.invalidateQueries(['myJobs']);
    },
    onError: (err) => {
      // Prefer the first field error over the generic "The given data was invalid."
      const data = err.response?.data;
      const fieldError = data?.errors ? Object.values(data.errors).flat()[0] : null;
      setError(fieldError || data?.message || ((isEditMode && !isDraft) ? 'Failed to update job' : 'Failed to create job'));
    },
  });

  const updateFormData = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setError(null);
  };

  const goToStep = (step) => {
    setCurrentStep(step);
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleExit = () => {
    if (loading || showProgressModal) return;
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.replace('MyJobs');
    }
  };

  const handleClose = () => {
    if (showConfirmModal) {
      setShowConfirmModal(false);
      return;
    }
    handleExit();
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      handleExit();
    }
  };

  // Calculate hours between start and end time for locum jobs
  const calculateLocumHours = () => {
    if (!formData.startTime || !formData.endTime) return 1;
    const parseTime = (timeStr) => {
      const match = timeStr.match(/(\d{1,2}):?(\d{2})?/);
      if (!match) return null;
      return parseInt(match[1]) + (parseInt(match[2] || '0') / 60);
    };
    const start = parseTime(formData.startTime);
    const end = parseTime(formData.endTime);
    if (start === null || end === null) return 1;
    let diff = end - start;
    if (diff <= 0) diff += 24;
    return diff;
  };

  // Map job type string to number (matching native PostJob.js)
  const getJobTypeNumber = () => {
    const typeMap = {
      'locum': 0,
      'fulltime': 1,
      'parttime': 2,
      'contract': 3,
      'international': 4,
    };
    return typeMap[formData.jobType] ?? 1;
  };

  // Resolve the expiration timestamp from the selected expiration date (fallback: now + 30 days)
  const getExpirationTimestamp = () => {
    const d = formData.expirationDate
      ? (formData.expirationDate instanceof Date ? formData.expirationDate : new Date(formData.expirationDate))
      : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    return d.getTime();
  };

  // Day-diff from now, sent as expiration_days for backend tracking (back-compat)
  const getExpirationDays = (ts) =>
    Math.max(1, Math.round((ts - Date.now()) / (24 * 60 * 60 * 1000)));

  const handleSubmit = async () => {
    if (!canProceed()) return;
    setLoading(true);
    setError(null);
    try {
      const jobTypeNum = getJobTypeNumber();

      const title = (formData.profession
        ? getLocalizedProfessionName(formData.profession, country, jobTypeNum)
        : (formData.specialization || formData.professionSub))
        || formData.title || editJobData?.title;

      // For non-locum jobs, the chosen expiration date drives the timestamps (matching native PostJob.js)
      const expirationTimestamp = getExpirationTimestamp();

      const jobData = {
        title,
        subtitle: formData.subtitle,
        description: formData.description,
        contact_person: formData.contactPerson?.trim() || null,
        profession_id: formData.profession?.id,
        expertise: formData.profession?.id,
        type: `${jobTypeNum}`,
        facilities: ['doctor-gp-general-practitioner'],
        is_cash: true,
        is_emergency: false,
        start_timestamp: expirationTimestamp,
        end_timestamp: expirationTimestamp + 3600000,
        city: clinicCity,
        state: clinicState,
        country: country,
      };

      if (isEditMode) {
        jobData.uuid = editJobData.uuid;
      }

      if (formData.jobType === 'locum') {
        const startDateTime = formData.startDate && formData.startTime
          ? new Date(`${formData.startDate}T${formData.startTime}:00`).getTime()
          : null;
        const endDate = formData.endDate || formData.startDate;
        const endDateTime = endDate && formData.endTime
          ? new Date(`${endDate}T${formData.endTime}:00`).getTime()
          : null;

        jobData.start_timestamp = startDateTime;
        jobData.end_timestamp = endDateTime;
        const ratePerHour = parseInt(formData.pricePerHour) || 0;
        jobData.rate_per_hour = ratePerHour;
        jobData.price = Math.round(ratePerHour * calculateLocumHours());
        jobData.is_emergency = false;
      } else if (formData.jobType === 'international') {
        // International job fields
        jobData.international = formData.targetCountry;
        if (formData.salary?.showSalary) {
          jobData.price = parseInt(formData.salary.min) || 0;
          jobData.price_max = parseInt(formData.salary.max) || 0;
        }
        jobData.expiration_days = getExpirationDays(expirationTimestamp);

        // Multi-country creation
        if (!isEditMode && formData.selectedJobCountries?.length > 1) {
          setShowProgressModal(true);
          setCreationProgress({
            total: formData.selectedJobCountries.length,
            completed: 0,
            countries: formData.selectedJobCountries.map(code => ({
              code,
              status: 'pending',
              error: null,
            })),
          });

          const results = await createJobsForCountries(
            jobData,
            formData.selectedJobCountries,
            (progress) => setCreationProgress(progress)
          );

          setCreationResults(results);

          // Invalidate caches if any jobs were created
          if (results.some(r => r.status === 'success')) {
            queryClient.invalidateQueries(['facilityJobs']);
            queryClient.invalidateQueries(['facilityOverview']);
            queryClient.invalidateQueries(['applications']);
            queryClient.invalidateQueries(['overview']);
            queryClient.invalidateQueries(['myJobs']);
          }

          setShowProgressModal(false);
          setShowSummaryModal(true);
          setLoading(false);
          return; // Don't use the normal mutation path
        }

        // Single country
        jobData.country = formData.selectedJobCountries?.[0] || country || 'MY';
      } else {
        // Non-locum, non-international
        if (formData.salary?.showSalary) {
          jobData.price = parseInt(formData.salary.min) || 0;
          jobData.price_max = parseInt(formData.salary.max) || 0;
        }
        jobData.expiration_days = getExpirationDays(expirationTimestamp);
      }

      await createJobMutation.mutateAsync(jobData);
      showToast((isEditMode && !isDraft) ? 'Job updated successfully!' : 'Job posted successfully!', 'success');
      setTimeout(() => handleExit(), 1500);
    } catch (err) {
      // Error handled in mutation
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!canProceed()) return;
    setLoading(true);
    setError(null);
    try {
      const jobTypeNum = getJobTypeNumber();

      const title = (formData.profession
        ? getLocalizedProfessionName(formData.profession, country, jobTypeNum)
        : (formData.specialization || formData.professionSub))
        || formData.title || editJobData?.title;

      const draftExpirationTimestamp = getExpirationTimestamp();

      const jobData = {
        title,
        subtitle: formData.subtitle,
        description: formData.description,
        contact_person: formData.contactPerson?.trim() || null,
        profession_id: formData.profession?.id,
        expertise: formData.profession?.id,
        type: `-1${jobTypeNum}`,
        facilities: ['doctor-gp-general-practitioner'],
        is_cash: true,
        is_emergency: false,
        start_timestamp: draftExpirationTimestamp,
        end_timestamp: draftExpirationTimestamp + 3600000,
        city: clinicCity,
        state: clinicState,
        country: country,
      };

      if (isEditMode) {
        jobData.uuid = editJobData.uuid;
      }

      if (formData.jobType === 'locum') {
        const startDateTime = formData.startDate && formData.startTime
          ? new Date(`${formData.startDate}T${formData.startTime}:00`).getTime()
          : null;
        const endDate = formData.endDate || formData.startDate;
        const endDateTime = endDate && formData.endTime
          ? new Date(`${endDate}T${formData.endTime}:00`).getTime()
          : null;

        jobData.start_timestamp = startDateTime;
        jobData.end_timestamp = endDateTime;
        const ratePerHour = parseInt(formData.pricePerHour) || 0;
        jobData.rate_per_hour = ratePerHour;
        jobData.price = Math.round(ratePerHour * calculateLocumHours());
        jobData.is_emergency = false;
      } else if (formData.jobType === 'international') {
        jobData.international = formData.targetCountry;
        if (formData.salary?.showSalary) {
          jobData.price = parseInt(formData.salary.min) || 0;
          jobData.price_max = parseInt(formData.salary.max) || 0;
        }
        jobData.expiration_days = getExpirationDays(draftExpirationTimestamp);
        jobData.country = formData.selectedJobCountries?.[0] || country || 'MY';
      } else {
        if (formData.salary?.showSalary) {
          jobData.price = parseInt(formData.salary.min) || 0;
          jobData.price_max = parseInt(formData.salary.max) || 0;
        }
        jobData.expiration_days = getExpirationDays(draftExpirationTimestamp);
      }

      await createJobMutation.mutateAsync(jobData);
      showToast('Draft saved successfully!', 'success');
      setTimeout(() => handleExit(), 1500);
    } catch (err) {
      // Error handled in mutation
    } finally {
      setLoading(false);
    }
  };

  // Handle retry for failed countries (multi-country creation)
  const handleRetryFailedCountries = async (failedCountries) => {
    setShowSummaryModal(false);
    setShowProgressModal(true);
    setCreationProgress({
      total: failedCountries.length,
      completed: 0,
      countries: failedCountries.map(code => ({
        code,
        status: 'pending',
        error: null,
      })),
    });

    try {
      const jobTypeNum = getJobTypeNumber();
      const title = (formData.profession
        ? getLocalizedProfessionName(formData.profession, country, jobTypeNum)
        : (formData.specialization || formData.professionSub))
        || formData.title;

      const retryExpirationTimestamp = getExpirationTimestamp();

      const jobData = {
        title,
        subtitle: formData.subtitle,
        description: formData.description,
        contact_person: formData.contactPerson?.trim() || null,
        profession_id: formData.profession?.id,
        expertise: formData.profession?.id,
        type: `${jobTypeNum}`,
        facilities: ['doctor-gp-general-practitioner'],
        is_cash: true,
        is_emergency: false,
        start_timestamp: retryExpirationTimestamp,
        end_timestamp: retryExpirationTimestamp + 3600000,
        international: formData.targetCountry,
        expiration_days: getExpirationDays(retryExpirationTimestamp),
        city: clinicCity,
        state: clinicState,
      };

      if (formData.salary?.showSalary) {
        jobData.price = parseInt(formData.salary.min) || 0;
        jobData.price_max = parseInt(formData.salary.max) || 0;
      }

      const results = await createJobsForCountries(
        jobData,
        failedCountries,
        (progress) => setCreationProgress(progress)
      );

      setCreationResults(results);

      // Invalidate caches if any jobs were created
      if (results.some(r => r.status === 'success')) {
        queryClient.invalidateQueries(['facilityJobs']);
        queryClient.invalidateQueries(['facilityOverview']);
        queryClient.invalidateQueries(['applications']);
        queryClient.invalidateQueries(['overview']);
        queryClient.invalidateQueries(['myJobs']);
      }

      setShowProgressModal(false);
      setShowSummaryModal(true);
    } catch (err) {
      setShowProgressModal(false);
      setError('Failed to retry job creation. Please try again.');
    }
  };

  const handleViewCreatedJobs = () => {
    setShowSummaryModal(false);
    handleExit();
  };

  const handleCloseSummary = () => {
    setShowSummaryModal(false);
    if (creationResults.some(r => r.status === 'success')) {
      handleExit();
    }
  };

  // Determine if we can proceed to next step
  const canProceed = () => {
    switch (currentStep) {
      case 0: return !!formData.jobType;
      case 1: return !!formData.profession?.id || !!formData.specialization;
      case 2:
        if (formData.jobType === 'locum') {
          if (!formData.startDate || !formData.startTime || !formData.endTime || !formData.pricePerHour) return false;
          // Validate end date/time is after start date/time
          const effectiveEndDate = formData.endDate || formData.startDate;
          if (effectiveEndDate < formData.startDate) return false;
          if (effectiveEndDate === formData.startDate && formData.endTime <= formData.startTime) return false;
          return true;
        }
        if (formData.jobType === 'international') {
          if (!formData.targetCountry) return false;
          if (!isEditMode && (!formData.selectedJobCountries || formData.selectedJobCountries.length === 0)) return false;
          return !!formData.description || !!formData.specialization;
        }
        return !!formData.description || !!formData.specialization;
      default: return false;
    }
  };

  const currentStepConfig = STEPS[currentStep];
  const CurrentStepComponent = currentStepConfig.component;
  const isLastStep = currentStep === STEPS.length - 1;

  return (
    <>
    <WebModal
      visible={true}
      onClose={handleClose}
      title={isEditMode && currentStepConfig.editTitle ? currentStepConfig.editTitle : currentStepConfig.title}
      subtitle={isEditMode && currentStepConfig.editSubtitle ? currentStepConfig.editSubtitle : currentStepConfig.subtitle}
      showBackButton={true}
      backAccessibilityLabel={currentStep === 0 ? 'Back to jobs' : 'Previous step'}
      onBack={handleBack}
      maxWidth={600}
      headerStyle={currentStep === 2 ? styles.postJobHeader : undefined}
      footer={
        <View>
          {error && (
            <Text style={styles.errorText}>{error}</Text>
          )}
          <WebModalFooter
            onBack={(isLastStep && !isPostedEdit) ? handleSaveDraft : handleBack}
            backLabel={(isLastStep && !isPostedEdit) ? 'Save Draft' : 'Back'}
            onNext={isLastStep ? () => { if (canProceed()) setShowConfirmModal(true); } : handleNext}
            nextLabel={isLastStep ? (loading ? ((isEditMode && !isDraft) ? 'Updating...' : 'Posting...') : ((isEditMode && !isDraft) ? 'Update Job' : 'Post Job')) : 'Next'}
            nextDisabled={!canProceed() || loading}
          />
        </View>
      }
      overlay={
        <>
          {toast && (
            <View style={[styles.toast, toast.type === 'success' && styles.toastSuccess, toast.type === 'error' && styles.toastError]}>
              <Text style={styles.toastText}>{toast.message}</Text>
            </View>
          )}
          {showConfirmModal && (
            <View style={styles.confirmOverlay}>
              <View style={styles.confirmModal}>
                <View style={{ alignItems: 'center', marginBottom: 12 }}>
                  <Text style={styles.confirmIcon}>⚠</Text>
                </View>
                <Text style={styles.confirmTitle}>Confirm Job</Text>
                <Text style={styles.confirmMessage}>
                  {isEditMode && !isDraft
                    ? 'Are you sure you want to edit this job?'
                    : 'By creating this job, it will be posted in the jobs page and you are required to pay the approved candidate.'}
                </Text>
                <View style={styles.confirmDivider} />
                <View style={styles.confirmButtons}>
                  <Text
                    style={styles.confirmCancel}
                    onPress={(event) => {
                      event.stopPropagation();
                      setShowConfirmModal(false);
                    }}
                  >
                    Cancel
                  </Text>
                  <View style={styles.confirmButtonDivider} />
                  <Text
                    style={styles.confirmConfirm}
                    onPress={() => {
                      setShowConfirmModal(false);
                      handleSubmit();
                    }}
                  >
                    Confirm
                  </Text>
                </View>
              </View>
            </View>
          )}
        </>
      }
    >
      <CurrentStepComponent
        formData={formData}
        updateFormData={updateFormData}
        goToStep={goToStep}
        currentStep={currentStep}
        clinicName={clinicName}
      />
    </WebModal>

      {/* Multi-country Job Creation Progress */}
      <WebJobCreationProgress
        visible={showProgressModal}
        progress={creationProgress}
        onClose={() => setShowProgressModal(false)}
      />

      {/* Multi-country Job Creation Summary */}
      <WebJobCreationSummary
        visible={showSummaryModal}
        results={creationResults}
        onClose={handleCloseSummary}
        onRetry={handleRetryFailedCountries}
        onViewJobs={handleViewCreatedJobs}
      />
    </>
  );
};

const styles = StyleSheet.create({
  postJobHeader: {
    backgroundColor: 'white',
  },
  errorText: {
    color: webColors.error,
    fontSize: 13,
    textAlign: 'center',
    marginBottom: 12,
  },
  toast: {
    position: 'fixed',
    top: 24,
    right: 24,
    zIndex: 10001,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: webBorderRadius.md,
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
    borderLeftWidth: 4,
    borderLeftColor: webColors.info,
  },
  toastSuccess: {
    borderLeftColor: webColors.success,
  },
  toastError: {
    borderLeftColor: webColors.error,
  },
  toastText: {
    fontSize: 14,
    color: webColors.textPrimary,
  },
  confirmOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
  },
  confirmModal: {
    backgroundColor: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 340,
    paddingTop: 24,
    overflow: 'hidden',
  },
  confirmIcon: {
    fontSize: 28,
    color: '#FF4B4B',
  },
  confirmTitle: {
    fontWeight: 'bold',
    fontSize: 17,
    textAlign: 'center',
    marginBottom: 8,
  },
  confirmMessage: {
    textAlign: 'center',
    fontSize: 14,
    color: '#555',
    marginHorizontal: 24,
    marginBottom: 20,
    lineHeight: 20,
  },
  confirmDivider: {
    height: 1,
    backgroundColor: '#E5E5E5',
  },
  confirmButtons: {
    flexDirection: 'row',
    height: 50,
  },
  confirmCancel: {
    flex: 1,
    textAlign: 'center',
    lineHeight: 50,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000',
    cursor: 'pointer',
  },
  confirmButtonDivider: {
    width: 1,
    backgroundColor: '#E5E5E5',
  },
  confirmConfirm: {
    flex: 1,
    textAlign: 'center',
    lineHeight: 50,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FF4B4B',
    cursor: 'pointer',
  },
});

export default PostJobScreen;
